import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from '../../../utils/baseQuery';

import type { ILabel } from '../labels.types';

type teamParamsType = {
  ProfileURL?: string;
  id?: string;
  name?: string;
  users?: [] | string[];
  userId?: [] | string[];
  status?: string;
};
type listParamsType = {
  filters?: [] | { fieldname: string; search?: string; sort?: number }[];
  globalSearch?: string;
  page?: number;
  limit?: number;
};

type addUserParamsType = {
  firstName: string;
  lastName: string;
  password: string;
  email: string;
};

type updateUserParamType = {
  id: string;
  ProfileURL?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  extNumber?: string;
  userExtId?: string;
  UserExtPassword?: string;
};

interface ITeamItemResponse {
  id: string;
  name: string;
  users: IUserItemResponse[] | [];
  createdAt: string;
  status: string;
}

interface IUserItemResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
}

interface IUserDtl {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  role: 'USER' | 'ADMIN' | 'OTHER_ROLE'; // Add other possible roles if needed
  teamId: string | null;
  createdAt: string;
  status: string;
  extNumber: string;
  userExtId: string;
  userExtPassword: string;
  ProfileURL?: string | null;
  roleId?: string;
  reportTo?: string;
  isEmailVerified?: boolean;
}

interface IWebhook {
  id: string;
  url: string;
  event: string;
  isActive: boolean;
  [key: string]: any;
}

interface IWebhookListResponse {
  data?: {
    WebhookSetting?: {
      list?: IWebhook[];
      total?: number;
    };
  };
}

const BE_URL_ROUTES = {
  // GROUPS
  LIST: '/team/list',
  CREATE: '/team/add',
  UPDATE: '/team/update',
  TEAM_DETAILS: '/team/details',
  // USERS
  USER_LIST: '/user/list',
  ADD_USER: '/user/add',
  USER_DETAILS: '/user/details',
  USER_UPDATE: '/user/update',
};

export const settingApi = createApi({
  reducerPath: 'settingApi',
  baseQuery,
  endpoints: (builder) => ({
    // #################### GROUPS ####################
    // Get TeamList
    getTeamList: builder.mutation({
      query: ({ filters, globalSearch, page = 1, limit = 10 }: listParamsType) => ({
        url: BE_URL_ROUTES.LIST,
        method: 'POST',
        body: { filters, globalSearch, page, limit },
      }),
      transformResponse: (response: {
        data?: { team?: { list?: ITeamItemResponse[]; total?: number } };
      }) => {
        const teamList = response?.data?.team?.list?.map((item) => ({
          id: item.id ?? '',
          teamName: item.name ?? '',
          noOfUsers: item.users.length ?? 0,
          users: item.users.map((user: any) => ({
            id: user.user.id ?? '',
            username: `${user.user.firstName ?? ''} ${user.user.lastName ?? ''}`,
            email: user.user.email ?? '',
            role: user.user.role ?? '',
            joinedDate: user.user.createdAt ?? '',
            status: user.user.status ?? 'INACTIVE',
          })),
          createdDate: item.createdAt ?? '',
          status: item.status,
        }));
        return { list: teamList ?? [], total: response?.data?.team?.total ?? 0 };
      },
    }),
    // Create Team
    createTeam: builder.mutation({
      query: ({ name, users, status }: teamParamsType) => ({
        url: BE_URL_ROUTES.CREATE,
        method: 'POST',
        body: { name, users, status },
      }),
    }),
    // Update Team
    updateTeam: builder.mutation({
      query: ({ id, name, users, status, ProfileURL }: teamParamsType) => ({
        url: BE_URL_ROUTES.UPDATE,
        method: 'POST',
        body: { id, name, userId: users, status, ProfileURL },
      }),
    }),
    // Filter Team List => Search, Sort, Filter
    getFilterTeamList: builder.mutation({
      query: ({ filters, globalSearch, page = 1, limit = 10 }: listParamsType) => ({
        url: BE_URL_ROUTES.LIST,
        method: 'POST',
        body: { filters, globalSearch, page, limit },
      }),
      transformResponse: (response: {
        data?: { team?: { list?: ITeamItemResponse[]; total?: number } };
      }) => {
        const teamList = response?.data?.team?.list?.map((item) => ({
          id: item.id ?? '',
          teamName: item.name ?? '',
          noOfUsers: item.users.length ?? 0,
          users: item.users.map((user: any) => ({
            id: user.user.id ?? '',
            username: `${user.user.firstName ?? ''} ${user.user.lastName ?? ''}`,
            email: user.user.email ?? '',
            role: user.user.role ?? '',
            joinedDate: user.user.createdAt ?? '',
            status: user.user.status ?? 'INACTIVE',
          })),
          createdDate: item.createdAt ?? '',
          updatedAt: item.createdAt ?? '',
          status: item.status,
        }));
        return { list: teamList ?? [], total: response?.data?.team?.total ?? 0 };
      },
    }),
    // Get Team Details
    getTeamDetails: builder.mutation({
      query: ({ id }: teamParamsType) => ({
        url: BE_URL_ROUTES.TEAM_DETAILS,
        method: 'POST',
        body: { id },
      }),
      transformResponse: (response: { data?: { team?: any } }) => response?.data?.team,
    }),
    // #################### USERS ####################
    // Get User list
    getUserList: builder.mutation({
      query: ({ filters = [], globalSearch = '', page = 1, limit = 10 }: listParamsType) => ({
        url: BE_URL_ROUTES.USER_LIST,
        method: 'POST',
        body: {
          filters,
          globalSearch,
          page,
          limit,
        },
      }),
      transformResponse: (response: { data?: { user?: { list?: any; total?: number } } }) => ({
        list: response?.data?.user?.list ?? [],
        total: response?.data?.user?.total ?? 0,
      }),
    }),
    // Create User
    createUser: builder.mutation({
      query: ({ firstName, lastName, password, email }: addUserParamsType) => ({
        url: BE_URL_ROUTES.ADD_USER,
        method: 'POST',
        body: { firstName, lastName, password, email },
      }),
    }),
    // Filter User List => Search, Sort, Filter
    filterUserDetail: builder.mutation({
      query: (filterQuery) => ({
        url: BE_URL_ROUTES.USER_LIST,
        method: 'POST',
        body: filterQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { user?: { list?: any; total?: number } } }) => ({
        list: response?.data?.user?.list ?? [],
        total: response?.data?.user?.total ?? 0,
      }),
    }),
    // update user deatil
    updateUser: builder.mutation({
      query: (updateQueary) => ({
        url: '/user/update',
        method: 'POST',
        body: updateQueary,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    // invite User
    addUser: builder.mutation({
      query: (newUserDetail) => ({
        url: 'user/add',
        method: 'POST',
        body: newUserDetail,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    resendMail: builder.mutation({
      query: (recordId) => ({
        url: 'user/inviteResend',
        method: 'POST',
        body: recordId,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    // Get User details (By Id)
    getUserDtl: builder.mutation({
      query: ({ id }) => ({
        url: BE_URL_ROUTES.USER_DETAILS,
        method: 'POST',
        body: { id },
      }),
      transformResponse: (response: { data?: { user: IUserDtl } }) => {
        const userResp = response.data?.user ?? null;
        const userDtl = {
          id: userResp?.id ?? '',
          firstName: userResp?.firstName ?? '',
          lastName: userResp?.lastName ?? '',
          email: userResp?.email ?? '',
          role: userResp?.role ?? '',
          status: userResp?.status ?? 'Active',
          extNumber: userResp?.extNumber ?? '',
          userExtId: userResp?.userExtId ?? '',
          userExtPassword: userResp?.userExtPassword ?? '',
          ProfileURL: userResp?.ProfileURL ?? '',
          reportTo: userResp?.reportTo ?? '',
          roleId: userResp?.roleId ?? '',
          isEmailVerified: userResp?.isEmailVerified ?? false,
          createdAt: userResp?.createdAt ?? '',
        };

        return userDtl;
      },
    }),
    // Update User Details (id, firstName, lastName, email, extNumber, PBXUserExt, UserExtPwd)
    updateUserDtl: builder.mutation({
      query: (updateUserParam: updateUserParamType) => ({
        url: BE_URL_ROUTES.USER_UPDATE,
        method: 'POST',
        body: updateUserParam,
      }),
    }),
    // Get Templates
    getTemplates: builder.mutation({
      query: (templateQuery) => ({
        url: 'template/list',
        method: 'POST',
        body: templateQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { messageTemplate?: { list?: any; total: number } };
      }) => ({
        list: response?.data?.messageTemplate?.list ?? [],
        totalPages: response?.data?.messageTemplate?.total ?? 0,
      }),
    }),
    filterTemplate: builder.mutation({
      query: (templateQuery) => ({
        url: 'template/list',
        method: 'POST',
        body: templateQuery,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: {
        data?: { messageTemplate?: { list?: any; total: number } };
      }) => ({
        list: response?.data?.messageTemplate?.list ?? [],
        totalPages: response?.data?.messageTemplate?.total ?? 0,
      }),
    }),
    syncTamplates: builder.mutation<ILabel, Partial<ILabel>>({
      query: (newAccount) => ({
        url: 'template/syncTemplate',
        method: 'POST',
        body: { channelId: newAccount },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    // label list
    getLabelList: builder.mutation({
      query: (messageFilter) => ({
        url: 'label/list',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { did?: any } }) => ({
        list: response?.data?.did?.list ?? [],
        total: response?.data?.did?.total ?? 0,
      }),
    }),

    createLabel: builder.mutation<ILabel, Partial<ILabel>>({
      query: (newAccount) => ({
        url: 'label/add',
        method: 'POST',
        body: newAccount,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { label: ILabel }; success: boolean }): ILabel =>
        response.success && response.data?.label ? response.data.label : ({} as ILabel),
    }),
    updateLabel: builder.mutation<ILabel, { id: string; updates: Partial<ILabel> }>({
      query: ({ id, updates }) => ({
        url: `label/update`,
        method: 'POST',
        body: { id, ...updates },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getLabelDetail: builder.mutation({
      query: (id: string) => ({
        url: 'label/details',
        method: 'POST',
        body: { id },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getNotificationList: builder.mutation({
      query: (messageFilter) => ({
        url: 'notification/list',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { notification?: any } }) => ({
        list: response?.data?.notification?.list ?? [],
        totalPages: response?.data?.notification?.totalPages ?? 0,
      }),
    }),
    notificationMarkAsRead: builder.mutation({
      query: (messageFilter) => ({
        url: 'notification/markAsRead',
        method: 'POST',
        body: messageFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    // DID list
    getDidList: builder.mutation({
      query: (didFilter) => ({
        url: 'did/list',
        method: 'POST',
        body: didFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { did?: { list?: any; total?: number } } }) => {
        const obj = {
          list:
            response?.data?.did?.list?.map((item: any) => ({
              ...item,
              // labels,
            })) ?? [],
          total: response?.data?.did?.total ?? 0,
        };

        return obj;
      },
    }),
    getDidNumber: builder.mutation({
      query: (didFilter) => ({
        url: 'billing/didsList',
        method: 'POST',
        body: didFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    addDid: builder.mutation({
      query: (didFilter) => ({
        url: 'did/add',
        method: 'POST',
        body: didFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    updateDid: builder.mutation({
      query: (didFilter) => ({
        url: 'did/update',
        method: 'POST',
        body: didFilter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getUserRolesList: builder.mutation({
      query: () => ({
        url: 'user/getRoles',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { roles?: any } }) => ({
        list: response?.data?.roles ?? [],
        // totalPages: response?.data?.roles?.totalPages ?? 0,
      }),
    }),

    // Billing
    // 1)Subscription
    subscriptionList: builder.mutation({
      query: (filter) => ({
        url: 'billing/subscriptionList',
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getPlanList: builder.mutation({
      query: (filter) => ({
        url: 'billing/planList',
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    buySubscription: builder.mutation({
      query: (filter) => ({
        url: 'billing/createSubscription',
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    // 2) Addons
    getAddonsList: builder.mutation({
      query: (filter) => ({
        url: 'billing/getAddOns',
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    buyAddons: builder.mutation({
      query: (filter) => ({
        url: 'billing/buyAddOns',
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    getWebhookList: builder.mutation<{ list: IWebhook[]; total: number }, any>({
      query: (filter) => ({
        url: 'webhooksetting/list',
        method: 'POST',
        body: filter,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    createWebhook: builder.mutation<IWebhook, Partial<IWebhook>>({
      query: (newWebhook) => ({
        url: 'webhooksetting/add',
        method: 'POST',
        body: newWebhook,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    updateWebhook: builder.mutation<IWebhook, { id: string; updates: Partial<IWebhook> }>({
      query: ({ id, updates }) => ({
        url: 'webhooksetting/update',
        method: 'POST',
        body: { id, ...updates },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    deleteWebhook: builder.mutation<{ success: boolean }, string[]>({
      query: (ids) => ({
        url: 'webhooksetting/delete',
        method: 'POST',
        body: { ids },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useGetTeamListMutation,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useGetFilterTeamListMutation,
  useGetTeamDetailsMutation,
  useGetUserListMutation,
  useCreateUserMutation,
  useGetTemplatesMutation,
  useFilterTemplateMutation,
  useGetUserDtlMutation,
  useUpdateUserDtlMutation,
  useGetLabelListMutation,
  useGetLabelDetailMutation,
  useAddUserMutation,
  useFilterUserDetailMutation,
  useResendMailMutation,
  useCreateLabelMutation,
  useUpdateLabelMutation,
  useUpdateUserMutation,
  useSyncTamplatesMutation,
  useGetNotificationListMutation,
  useNotificationMarkAsReadMutation,
  useGetDidListMutation,
  useGetDidNumberMutation,
  useAddDidMutation,
  useUpdateDidMutation,
  useGetUserRolesListMutation,
  //Billing
  // 1)Subscription
  useSubscriptionListMutation,
  useGetPlanListMutation,
  useBuySubscriptionMutation,
  // 2) Addons
  useGetAddonsListMutation,
  useBuyAddonsMutation,
  // webhook
  useGetWebhookListMutation,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
} = settingApi;
